import * as React from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import { Box, Grid, Divider, IconButton, Tooltip } from "@mui/material";
import InstagramIcon from '@mui/icons-material/Instagram';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import { useEffect, useState } from "react";
import axios from 'axios';

const footerLinks = [
  { label: 'Beranda', href: '/beranda', icon: <HomeOutlinedIcon fontSize="small" /> },
  { label: 'Destinasi', href: '/destinasi', icon: <ExploreOutlinedIcon fontSize="small" /> },
  { label: 'Pencarian', href: '/pencarian', icon: <SearchOutlinedIcon fontSize="small" /> },
  { label: 'Berita', href: '/berita', icon: <NewspaperOutlinedIcon fontSize="small" /> },
];

export default function Footer() {
  const API_URL = process.env.REACT_APP_API;
  const BACKEND_API = process.env.REACT_APP_BE;
  const [beranda, setBeranda] = useState([]);

  const getBeranda = async () => {
    try {
      await axios.get(`${API_URL}beranda/allBeranda`)
        .then(({ data }) => { setBeranda(data?.data); })
        .catch((err) => { if (err.response.status === 404) { setBeranda([]); } });
    } catch (error) { console.log(error); }
  };

  useEffect(() => {
    getBeranda();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #2C1810 60%, #1A1A2E 100%)',
        pt: 6,
        pb: 3,
        mt: 4,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative gradient orb */}
      <Box sx={{
        position: 'absolute', top: '-20%', right: '-5%', width: 350, height: 350,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(184,92,44,0.12) 0%, transparent 70%)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 5 } }}>
        <Grid container spacing={5} justifyContent="center">

          {/* Brand Column */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: '10px',
                background: 'linear-gradient(135deg, #B85C2C, #F5A623)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <RestaurantOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              {beranda[0]?.logo && (
                <img
                  src={`${BACKEND_API}${beranda[0]?.logo}`}
                  alt={beranda[0]?.judul || 'Logo'}
                  style={{
                    maxHeight: '40px',
                    maxWidth: '160px',
                    objectFit: 'contain',
                    filter: 'brightness(0) invert(1)',
                    opacity: 0.9,
                  }}
                />
              )}
            </Box>
            <Typography sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '14px',
              lineHeight: 1.7,
              mb: 3,
              maxWidth: 320,
            }}>
              Portal gastronomi resmi yang menyajikan informasi kuliner dan wisata kuliner Bandung Barat secara lengkap dan terpercaya.
            </Typography>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {beranda[0]?.instagram && (
                <Tooltip title="Instagram" arrow>
                  <IconButton
                    component="a"
                    href={beranda[0]?.instagram}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      width: 40, height: 40,
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: 'rgba(255,255,255,0.7)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #B85C2C, #F5A623)',
                        borderColor: 'transparent',
                        color: '#fff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(184,92,44,0.4)',
                      }
                    }}
                  >
                    <InstagramIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>

          {/* Nav Links Column */}
          <Grid item xs={12} md={3}>
            <Typography sx={{
              color: '#F5A623',
              fontWeight: 700,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 2.5,
            }}>
              Navigasi
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {footerLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  underline="none"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      color: '#F5A623',
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  <Box sx={{ color: 'inherit', display: 'flex', alignItems: 'center' }}>
                    {link.icon}
                  </Box>
                  {link.label}
                </Link>
              ))}
            </Box>
          </Grid>

          {/* Info Column */}
          <Grid item xs={12} md={4}>
            <Typography sx={{
              color: '#F5A623',
              fontWeight: 700,
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              mb: 2.5,
            }}>
              Tentang
            </Typography>
            <Typography sx={{
              color: 'rgba(255,255,255,0.55)',
              fontSize: '14px',
              lineHeight: 1.7,
              mb: 2,
            }}>
              {beranda[0]?.kabupatenkota && `📍 ${beranda[0]?.kabupatenkota}`}
            </Typography>
            <Box sx={{
              p: 2,
              borderRadius: '12px',
              background: 'rgba(184,92,44,0.1)',
              border: '1px solid rgba(184,92,44,0.2)',
            }}>
              <Typography sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '13px',
                lineHeight: 1.6,
              }}>
                🍽️ Temukan dan eksplorasi kekayaan kuliner Bandung Barat bersama kami.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Divider & Copyright */}
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 5, mb: 3 }} />

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px' }}>
            © {new Date().getFullYear()}{' '}
            <Link
              href={window.location.protocol}
              color="inherit"
              underline="hover"
              sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#F5A623' } }}
            >
              {localStorage.getItem('judul') !== null ? localStorage.getItem('judul') : 'Gastronesia'}
            </Link>
            . All rights reserved.
          </Typography>
          <Typography sx={{
            color: 'rgba(255,255,255,0.25)',
            fontSize: '12px',
          }}>
            Dibuat dengan ❤️ untuk kuliner Bandung Barat
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}